import React, { useCallback, useEffect, useState } from 'react';
import { FiUsers, FiX } from 'react-icons/fi';
import { socialApi } from '../../support/supportApi';
import { normalizeMessagingUser } from './messagingUserUtils';
import MessagingPeopleSkeleton from './MessagingPeopleSkeleton';
import { applyProfileAvatarImgFallback } from '../profilePictureUrl';
import './Messaging.css';

function FollowList({ users, emptyLabel, onSelectUser }) {
  if (!users.length) {
    return <p className="messaging-follow-sheet__empty">{emptyLabel}</p>;
  }
  return (
    <ul className="messaging-follow-sheet__list">
      {users.map((user) => {
        const normalized = normalizeMessagingUser(user);
        if (!normalized) return null;
        return (
        <li key={normalized.userId}>
          <button
            type="button"
            className="messaging-follow-sheet__user noOutline-text"
            onClick={() => onSelectUser?.(normalized)}
          >
            <img
              src={normalized.profilePictureUrl}
              alt=""
              className="messaging-follow-sheet__avatar"
              onError={(e) => {
                applyProfileAvatarImgFallback(e.currentTarget, {
                  userId: normalized.userId,
                  name: normalized.displayName,
                });
              }}
            />
            <span className="messaging-follow-sheet__name">
              {normalized.displayName}
            </span>
          </button>
        </li>
        );
      })}
    </ul>
  );
}

/**
 * Mobile sheet for viewing followers / following from the floating chrome.
 */
export default function MessagesFollowSheet({ open, onClose, viewerUserId, onSelectUser }) {
  const [tab, setTab] = useState('followers');
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadLists = useCallback(async () => {
    if (!viewerUserId) return;
    setLoading(true);
    try {
      const [followersRes, followingRes] = await Promise.all([
        socialApi.followers({ userId: viewerUserId, profileMode: 'personal', skip: 0, limit: 60 }),
        socialApi.following({ userId: viewerUserId, profileMode: 'personal', skip: 0, limit: 60 }),
      ]);
      setFollowers(followersRes?.users || []);
      setFollowing(followingRes?.users || []);
    } catch {
      setFollowers([]);
      setFollowing([]);
    } finally {
      setLoading(false);
    }
  }, [viewerUserId]);

  useEffect(() => {
    if (open) loadLists();
  }, [open, loadLists]);

  if (!open) return null;

  return (
    <div className="messaging-follow-sheet" role="dialog" aria-label="Followers and following">
      <div className="messaging-follow-sheet__backdrop" onClick={onClose} aria-hidden />
      <div className="messaging-follow-sheet__panel">
        <div className="messaging-follow-sheet__header">
          <FiUsers aria-hidden />
          <h2>Connections</h2>
          <button type="button" className="messaging-follow-sheet__close noOutline-text" onClick={onClose} aria-label="Close">
            <FiX aria-hidden />
          </button>
        </div>
        <div className="messaging-follow-sheet__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'followers'}
            className={`messaging-follow-sheet__tab noOutline-text${tab === 'followers' ? ' is-active' : ''}`}
            onClick={() => setTab('followers')}
          >
            Followers ({followers.length})
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'following'}
            className={`messaging-follow-sheet__tab noOutline-text${tab === 'following' ? ' is-active' : ''}`}
            onClick={() => setTab('following')}
          >
            Following ({following.length})
          </button>
        </div>
        <div className="messaging-follow-sheet__body">
          {loading ? (
            <MessagingPeopleSkeleton
              rows={6}
              listClassName="messaging-follow-sheet__list messaging-follow-sheet__list--skeleton"
            />
          ) : tab === 'followers' ? (
            <FollowList users={followers} emptyLabel="No followers yet." onSelectUser={onSelectUser} />
          ) : (
            <FollowList users={following} emptyLabel="Not following anyone yet." onSelectUser={onSelectUser} />
          )}
        </div>
      </div>
    </div>
  );
}
